// Order List — mobile-first.
//
// The card grid is the layout, not a fallback: a phone shows one column, and
// wider screens get two then three of the same card. There is no separate
// desktop table to keep in sync, which is what let the old DataTable drift
// (its tab strip set activeTab and nothing ever read it).
//
// Field names below are the ones GetRequestListGen actually returns — note
// that the money and payment columns are `totalamount` / `paymentstatus`
// WITHOUT the res- prefix the other order columns carry, and that menu items
// arrive as eight flattened column triplets rather than a nested array.

import React, { Component } from "react";
import { connect } from "react-redux";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import jsPDF from "jspdf";
import "jspdf-autotable";

import * as actions from "../../../store/actions/index";
import { ORDER_TYPES } from "../../Layout/POSSidebar";
import { POS_GUEST_SESSION_KEY } from "../../../shared/Config";

const PAGE_SIZE = 12;

// The special-order guest: SpecialOrderDetails.js signs a customer's own phone
// in as 9999, and this page is not for that identity — it signs in as 1001
// instead, exactly as it does when nobody is logged in at all.
const SPECIAL_ORDER_GUEST_EMP_ID = "35ad8f97-36e2-46c6-a602-29b2d2d36f11";

// Mirrors POSApp's own check: a token only counts while it is unexpired.
// EmpId is not a session — it outlives the token by hours, and reading it as
// "somebody is logged in" is what left this page with no identity at all: the
// login was skipped because EmpId was there, POSApp skipped authCheckState
// because the token was dead, so userDetail stayed null and the order API was
// never called. No request, no error, just an empty list.
const hasLiveSession = () => {
    if (!localStorage.getItem("token")) return false;
    return new Date(localStorage.getItem("expirationDate")) > new Date();
};

// Order-type filter. Values are the requestType GUIDs from POSSidebar's
// ORDER_TYPES; "" means all types.
const ORDER_TYPE_OPTIONS = [
    { label: "All order types", value: "" },
    { label: "Dine-in", value: ORDER_TYPES.DINE_IN },
    { label: "Takeaway", value: ORDER_TYPES.TAKEAWAY },
    { label: "Delivery", value: ORDER_TYPES.DELIVERY },
    { label: "Quick order", value: ORDER_TYPES.QUICK_ORDER },
];

// The response flattens the cart into eight fixed slots. An order with more
// than eight distinct items is truncated by the API, not here.
const MAX_ITEM_SLOTS = 8;

const FIELD_KEYS = {
    orderNum: ["requestNum"],
    customerName: ["rescustomername"],
    phone: ["resphone"],
    address: ["resdeliveryaddress"],
    // resdeliverydate is null on every order placed before that column was
    // added to the save payload. fromDate is the sound fallback: the order
    // screens set FromDate and ToDate to the delivery date, which is why row 2
    // of the sample is dated 2026-07-29 but carries fromDate 2026-08-12.
    deliveryDate: ["resdeliverydate"],
    deliveryTime: ["resdeliverytime"],
    amount: ["totalamount"],
    paymentStatus: ["paymentstatus"],
};

const pick = (row, keys) => {
    if (!row) return null;
    for (const key of keys) {
        const value = row[key];
        // Zero is a legitimate amount, so only null/undefined/"" are misses.
        if (value !== undefined && value !== null && value !== "") return value;
    }
    return null;
};

const DASH = "—";

const isBlank = (v) => v === undefined || v === null || v === "";

const show = (value) => (isBlank(value) ? DASH : String(value));

const fmtMoney = (value) => {
    // Guarded before Number(), which turns null into 0 — an order whose amount
    // the API never sent would otherwise read as genuinely free.
    if (isBlank(value)) return DASH;
    const n = Number(value);
    return Number.isFinite(n) ? `₹${n.toFixed(2)}` : DASH;
};

// Tolerates the ISO timestamps this endpoint returns and the MM/DD/YYYY the
// save payload sends.
const fmtDate = (value) => {
    if (isBlank(value)) return DASH;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString();
};

// PDF-safe variants of the display formatters.
//
// jsPDF's built-in Helvetica is WinAnsi-encoded and has no glyph for either the
// rupee sign (U+20B9) or the em dash used on screen, so both come out as
// mojibake in the document. The PDF gets a plain hyphen for blanks and bare
// numerals under an "Amount (Rs.)" heading instead.
const PDF_BLANK = "-";

const pdfText = (value) => (isBlank(value) ? PDF_BLANK : String(value));

const pdfAmount = (value) => {
    if (isBlank(value)) return PDF_BLANK;
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : PDF_BLANK;
};

const pdfDate = (value) => {
    if (isBlank(value)) return PDF_BLANK;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString();
};

// Local-day key, YYYY-MM-DD.
//
// Deliberately built from the local date components rather than
// toISOString().slice(0,10): the endpoint's timestamps carry no timezone
// suffix ("2026-08-04T00:00:00") so they parse as local midnight, and in any
// zone east of UTC — IST included — toISOString would roll that back to the
// previous day and file every order one day early.
const dayKey = (value) => {
    if (isBlank(value)) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;

    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
};

// resmenuitemN holds the menu item's uniqueId (a GUID like
// "d5ef0f15-361a-4596-8d6c-2bbacc45adfc"), NOT its name — the label comes from
// the MenuItem master, which getMasterKeywordList('MenuItem') puts in
// state.keyword.MenuItem as { label: text, value: uniqueId, id }.
//
// Matched on `value`/`uniqueId` only, never on `id`: the master's `id` is 1 for
// a great many rows, so matching it would label items at random. Compared
// case-insensitively, since GUID casing is not consistent across this
// backend's endpoints.
const menuNameFor = (menuItems, menuId) => {
    if (isBlank(menuId)) return null;

    const target = String(menuId).toLowerCase();
    const hit = (menuItems || []).find(
        (m) =>
            String(m.value).toLowerCase() === target ||
            String(m.uniqueId).toLowerCase() === target
    );
    if (hit) return hit.label || hit.text || hit.name || null;

    // Master still loading, or the item has since been removed from it. The
    // short id keeps the line visible and traceable rather than dropping it.
    return `Item ${String(menuId).slice(0, 8)}`;
};

// resmenuitem1..8 / resmenuqty1..8 / resmenuprice1..8. requestAttributes comes
// back empty on this endpoint, so these columns are the only item source.
// Slots are sparse — every one is checked and the empty ones dropped.
const getItems = (row, menuItems) => {
    if (!row) return [];

    const items = [];
    for (let i = 1; i <= MAX_ITEM_SLOTS; i += 1) {
        const menuId = row[`resmenuitem${i}`];
        if (isBlank(menuId)) continue;
        // A line reduced to qty 0 stays on the order (RestaurantOrder keeps a
        // saved delivery line at 0 rather than deleting it) but is no longer part
        // of it, so it is left off the cards, the View modal and the PDF alike.
        // A blank qty is not a 0 and still shows.
        const qty = row[`resmenuqty${i}`];
        if (!isBlank(qty) && Number(qty) === 0) continue;
        items.push({
            name: menuNameFor(menuItems, menuId),
            menuId,
            qty,
            price: row[`resmenuprice${i}`],
        });
    }
    return items;
};

// "Idly 1, Dosa 4"
const formatItems = (row, menuItems) => {
    const items = getItems(row, menuItems);
    if (!items.length) return null;
    return items
        .map((it) => (isBlank(it.qty) ? it.name : `${it.name} ${it.qty}`))
        .join(", ");
};

// Status is free text from the workflow (the sample alone carries "Closed" and
// "KOT-READY"), so the tab strip is built from the data rather than hardcoded.
//
// Running takes the fanta accent — it is the live state, and the theme's
// accent is what the eye should land on first. Closed and Cancelled keep their
// conventional green and red, warmed a touch to sit on the off-white ground.
const STATUS_COLORS = {
    Closed: { bg: "#e6f5ea", color: "#1d6b39" },
    Running: { bg: "#fff0e9", color: "#c2400f" },
    Cancelled: { bg: "#fdeaea", color: "#a32020" },
};

// Unknown statuses (the workflow emits free text, e.g. KOT-READY) fall back to
// the theme's secondary blue rather than being mistaken for one of the above.
const statusStyle = (status) => STATUS_COLORS[status] || { bg: "#e7f0fa", color: "#125ea3" };

// Scoped to .ol-root so nothing here can reach the rest of the POS. Real media
// queries rather than primeflex classes: this project is on primeflex
// 2.0.0-rc.1, whose responsive class names differ from current docs.
//
// Chenthoor palette, mirrored from src/POS/styles/_variables.scss — fanta
// orange as the primary accent over a warm off-white, blue kept as the
// secondary. Redeclared as custom properties here because this page styles
// itself from a JS string and cannot read the SCSS variables; if the theme
// moves, these six --ol-* values are the whole surface to update.
const STYLES = `
.ol-root {
  --ol-accent:#ff5a1f; --ol-accent-dark:#e04711; --ol-accent-soft:#fff0e9;
  --ol-ink:#2b2119; --ol-muted:#8a7b70; --ol-line:#ece3db;
  --ol-surface:#fff; --ol-ground:#faf7f4;

  background:var(--ol-ground); min-height:100%; padding:14px; color:var(--ol-ink);
  -webkit-font-smoothing:antialiased;
}

.ol-bar { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.ol-title { font-size:20px; font-weight:800; margin:0; flex:1 1 auto; letter-spacing:-.02em; }
/* The accent rule under the title is what marks the page as Chenthoor's
   rather than generic admin furniture. */
.ol-title:before {
  content:""; display:block; width:34px; height:3px; border-radius:3px;
  background:linear-gradient(90deg,var(--ol-accent),var(--ol-accent-dark)); margin-bottom:7px;
}
.ol-bar-actions { display:flex; gap:8px; flex:0 0 auto; }

.ol-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:14px; }
.ol-stat {
  background:var(--ol-surface); border:1px solid var(--ol-line); border-radius:14px;
  padding:12px 8px; text-align:center; position:relative; overflow:hidden;
  box-shadow:0 1px 2px rgba(43,33,25,.04);
}
.ol-stat:first-child:after {
  content:""; position:absolute; inset:0 auto 0 0; width:3px;
  background:linear-gradient(180deg,var(--ol-accent),var(--ol-accent-dark));
}
.ol-stat-label { font-size:10.5px; color:var(--ol-muted); text-transform:uppercase; letter-spacing:.06em; font-weight:600; }
.ol-stat-value { font-size:22px; font-weight:800; line-height:1.25; letter-spacing:-.02em; }

.ol-tabs { display:flex; gap:6px; overflow-x:auto; margin-bottom:14px; padding-bottom:2px; -webkit-overflow-scrolling:touch; }
.ol-tabs::-webkit-scrollbar { display:none; }
.ol-tab {
  flex:0 0 auto; min-width:86px; text-align:center; padding:9px 16px; border-radius:999px;
  cursor:pointer; font-size:13px; font-weight:600; white-space:nowrap;
  background:var(--ol-surface); color:var(--ol-muted); border:1px solid var(--ol-line);
  transition:background .18s ease, color .18s ease, border-color .18s ease, transform .18s ease;
}
.ol-tab:hover { color:var(--ol-accent-dark); border-color:#f6cdb9; background:var(--ol-accent-soft); }
.ol-tab-active, .ol-tab-active:hover {
  background:linear-gradient(135deg,var(--ol-accent),var(--ol-accent-dark));
  color:#fff; border-color:transparent;
  box-shadow:0 4px 12px -4px rgba(255,90,31,.55);
}

.ol-search { display:flex; gap:8px; margin-bottom:9px; }
.ol-search .p-inputtext { width:100%; border-radius:11px; border-color:var(--ol-line); }
.ol-search .p-inputtext:enabled:focus { box-shadow:0 0 0 3px rgba(255,90,31,.16); border-color:var(--ol-accent); }
.ol-search-field { flex:1 1 auto; }

.ol-filters { display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-bottom:14px; }
.ol-date { flex:1 1 170px; min-width:0; }
.ol-date .p-calendar, .ol-date .p-inputtext { width:100%; }
.ol-date .p-inputtext { border-radius:11px; border-color:var(--ol-line); }
.ol-type { flex:1 1 160px; min-width:0; }
.ol-type .p-dropdown { width:100%; border-radius:11px; border-color:var(--ol-line); }
.ol-type .p-dropdown:not(.p-disabled).p-focus { box-shadow:0 0 0 3px rgba(255,90,31,.16); border-color:var(--ol-accent); }
.ol-filter-note { font-size:12px; color:var(--ol-muted); flex:1 1 100%; }

.ol-grid { display:grid; grid-template-columns:1fr; gap:12px; align-items:start; }

.ol-card {
  background:var(--ol-surface); border:1px solid var(--ol-line); border-radius:16px; padding:14px;
  display:flex; flex-direction:column; gap:9px; position:relative; overflow:hidden;
  box-shadow:0 1px 2px rgba(43,33,25,.04);
  transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}
.ol-card:before {
  content:""; position:absolute; inset:0 0 auto 0; height:3px;
  background:linear-gradient(90deg,var(--ol-accent),var(--ol-accent-dark));
  opacity:0; transition:opacity .18s ease;
}
.ol-card:hover { transform:translateY(-2px); border-color:#f6d9c9;
                 box-shadow:0 10px 26px -14px rgba(43,33,25,.28); }
.ol-card:hover:before { opacity:1; }

.ol-card-top { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; }
.ol-num { font-size:15px; font-weight:800; letter-spacing:-.01em; word-break:break-all; }
.ol-badge { font-size:10.5px; font-weight:700; padding:4px 10px; border-radius:999px;
            white-space:nowrap; text-transform:uppercase; letter-spacing:.04em; }

.ol-row { display:flex; gap:9px; font-size:13px; color:#5a4b40; align-items:flex-start; }
.ol-row i { color:var(--ol-accent); font-size:12px; margin-top:3px; flex:0 0 auto; opacity:.85; }
.ol-row span { word-break:break-word; min-width:0; }

.ol-items {
  background:var(--ol-accent-soft); border:1px solid #fadcce; border-radius:11px;
  padding:8px 11px; font-size:12.5px; color:#7a3d22; word-break:break-word; line-height:1.5;
}

.ol-card-bottom { display:flex; align-items:center; justify-content:space-between; gap:8px;
                  border-top:1px solid var(--ol-line); padding-top:11px; margin-top:auto; }
.ol-price { font-size:18px; font-weight:800; color:var(--ol-ink); letter-spacing:-.02em; }
.ol-actions { display:flex; gap:6px; }
.ol-actions .p-button { border-radius:10px; }

.ol-empty { background:var(--ol-surface); border:1px dashed var(--ol-line); border-radius:16px;
            padding:44px 16px; text-align:center; color:var(--ol-muted); }
.ol-more { display:flex; justify-content:center; margin-top:16px; }
/* Problem notice: a failed fetch and a missing session both used to look
   exactly like a quiet day, so neither was ever noticed. */
.ol-notice { display:flex; flex-wrap:wrap; align-items:center; gap:10px; margin-bottom:14px;
             background:#fff4f2; border:1px solid #f0c5bb; border-radius:14px;
             padding:12px 14px; color:#8a2b17; font-size:13px; line-height:1.45; }
.ol-notice-text { flex:1 1 200px; min-width:0; }

.ol-dl { display:grid; grid-template-columns:1fr; gap:12px; }
.ol-dt { font-size:10.5px; text-transform:uppercase; letter-spacing:.06em; color:var(--ol-muted);
         font-weight:700; margin-bottom:3px; }
.ol-dd { font-size:14px; color:var(--ol-ink); word-break:break-word; }

.ol-itable { width:100%; border-collapse:collapse; font-size:13px; }
.ol-itable th { text-align:left; font-size:10.5px; text-transform:uppercase; letter-spacing:.06em;
                color:var(--ol-muted); font-weight:700; padding:6px 0; border-bottom:1px solid var(--ol-line); }
.ol-itable td { padding:7px 0; border-bottom:1px solid #f7f2ed; }
.ol-itable tr:last-child td { border-bottom:none; }
.ol-itable .ol-n { text-align:right; white-space:nowrap; font-variant-numeric:tabular-nums; }

/* Phone-only: keep the action buttons thumb-sized. */
@media (max-width:479px) {
  .ol-actions .p-button { min-width:44px; min-height:38px; }
}

@media (min-width:640px) {
  .ol-root { padding:18px; }
  .ol-title { font-size:23px; }
  .ol-grid { grid-template-columns:repeat(2,1fr); }
  .ol-dl { grid-template-columns:repeat(2,1fr); }
  .ol-stat-value { font-size:26px; }
}

@media (min-width:1024px) {
  .ol-root { padding:22px 26px; }
  .ol-grid { grid-template-columns:repeat(3,1fr); }
  .ol-stats { max-width:640px; }
}

/* Respect a reduced-motion preference: the lift and fades are decoration. */
@media (prefers-reduced-motion:reduce) {
  .ol-card, .ol-card:before, .ol-tab { transition:none; }
  .ol-card:hover { transform:none; }
}
`;

class OrderList extends Component {
    constructor(props) {
        super(props);

        this.state = {
            orders: [],
            // The status itself, not a tab index: the tab strip is derived from
            // the data, so an index would point at a different tab once a
            // refresh brings in an order with a status not seen before.
            activeStatus: "",
            // "" = all order types; otherwise a requestType GUID.
            orderType: "",
            search: "",
            // Defaults to today — a till wants the day's deliveries on open.
            // Null means every date; the button beside the picker toggles it.
            deliveryDate: new Date(),
            visibleCount: PAGE_SIZE,
            viewOrder: null,
        };
    }

    componentDidMount() {
        this.ensureSession();

        const empId = this.props.userDetail && this.props.userDetail.employeeId;
        if (empId) this.fetchOrders(empId);

        // resmenuitemN is a menu-item uniqueId; this master turns it into a
        // name. Cached in localStorage by the action, so this is cheap on
        // every visit after the first.
        this.props.getMasterKeywordList('MenuItem');
    }

    componentDidUpdate(prevProps) {
        const prevEmpId = prevProps.userDetail && prevProps.userDetail.employeeId;
        const currEmpId = this.props.userDetail && this.props.userDetail.employeeId;

        // On any change of identity, not just the first one to arrive: a
        // session re-established after an expiry (see ensureSession) brings
        // the same id back after a null, and a different user brings another
        // one. Keyed on "first appearance" alone, neither refetched.
        if (currEmpId && currEmpId !== prevEmpId) this.fetchOrders(currEmpId);

        if (prevProps.orderList !== this.props.orderList && this.props.orderList) {
            this.setState({ orders: this.props.orderList, visibleCount: PAGE_SIZE });
        }
    }

    // Signs this device in as 1001 when it has no usable identity: no live
    // session, or the special-order guest. A live staff session is left
    // alone — auth() overwrites the stored token and EmpId, which would swap
    // a cashier's identity for the guest account mid-shift.
    //
    // Attempted once per visit. userDetail arriving is what triggers the
    // fetch, so a login that fails leaves it null; without this flag the
    // update above would ask for another login on every render.
    // Returns whether a login was started.
    ensureSession = () => {
        if (this.sessionRequested) return false;
        if (hasLiveSession() && localStorage.getItem('EmpId') !== SPECIAL_ORDER_GUEST_EMP_ID) {
            return false;
        }

        this.sessionRequested = true;
        // Marks the session as a guest one, which keeps POSApp from wrapping
        // this page in the staff shell.
        localStorage.setItem(POS_GUEST_SESSION_KEY, "1");
        this.props.onAuth('1001', '456123789', false, false, false);
        return true;
    };

    fetchOrders = (empId) => {
        const id = empId || (this.props.userDetail && this.props.userDetail.employeeId);
        if (!id) return;

        this.props.getOrderList(
            id,
            "f7405b60-9933-4b08-a8be-107aa21a05f7",
            this.props.userDetail.companyId,
        );
    };

    // The old Refresh button called getOrderList with only the employee id, so
    // the action's `if (id && processId)` guard failed it every time and the
    // button silently did nothing. Routed through fetchOrders so it passes the
    // same three arguments the mount path does.
    // Also the way back from a session that expired while this page sat open:
    // there is no employee id left, so fetchOrders would quietly do nothing.
    // Signing in again is enough — the fetch follows from the id arriving.
    handleRefresh = () => {
        this.sessionRequested = false;
        if (this.ensureSession()) return;
        this.fetchOrders();
    };

    getTabs = () => {
        const seen = [];
        (this.state.orders || []).forEach((o) => {
            if (o.status && !seen.includes(o.status)) seen.push(o.status);
        });
        seen.sort();
        return [{ label: "All", value: "" }, ...seen.map((s) => ({ label: s, value: s }))];
    };

    getFilteredOrders = () => {
        const { orders, activeStatus, search, deliveryDate, orderType } = this.state;
        const q = search.trim().toLowerCase();
        const wantDay = deliveryDate ? dayKey(deliveryDate) : null;

        return (orders || []).filter((order) => {
            if (activeStatus && order.status !== activeStatus) return false;

            // requestType GUIDs aren't consistently cased across endpoints.
            if (orderType && String(order.requestType || "").toLowerCase() !== orderType.toLowerCase()) {
                return false;
            }

            // Compared day-to-day, so the picker's midnight Date still matches
            // a delivery date carrying a time. An order with no resolvable
            // date at all drops out while a day is selected.
            if (wantDay && dayKey(pick(order, FIELD_KEYS.deliveryDate)) !== wantDay) return false;

            if (!q) return true;

            // Searches what the card actually shows, so a hit is always
            // visible on the card that matched.
            const haystack = [
                pick(order, FIELD_KEYS.orderNum),
                pick(order, FIELD_KEYS.customerName),
                pick(order, FIELD_KEYS.phone),
                pick(order, FIELD_KEYS.address),
                formatItems(order, this.props.MenuItem),
                order.remark,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(q);
        });
    };

    // Says why the list is empty when the reason isn't "no orders that day":
    // nobody signed in on this device, or the fetch itself failed. Both used
    // to render as a plain empty grid, indistinguishable from a quiet day,
    // which is how an expired session went unnoticed for hours.
    renderProblemNotice = () => {
        if (this.props.loading) return null;

        const empId = this.props.userDetail && this.props.userDetail.employeeId;
        const message = !empId
            ? "Not signed in on this device, so no orders could be loaded."
            // The fetch error is only worth showing while there is nothing to
            // show: with orders on screen it is an older failure the list has
            // already recovered from.
            : (!this.state.orders.length && this.props.error ? String(this.props.error) : null);

        if (!message) return null;

        return (
            <div className="ol-notice">
                <span className="ol-notice-text">{message}</span>
                <Button
                    label={empId ? "Retry" : "Sign in again"}
                    icon="pi pi-refresh"
                    className="p-button-sm p-button-outlined"
                    onClick={this.handleRefresh}
                />
            </div>
        );
    };

    goToEdit = (row) => {
        // Same route the old eye button used: /pos/order/:requestid renders
        // RestaurantOrder, which is the order edit screen (POSApp.js).
        if (row && row.requestId) this.props.history.push(`/pos/order/${row.requestId}`);
    };

    generatedBy = () => {
        const d = this.props.userDetail || {};
        const u = this.props.userObject || {};
        const joined = [d.firstName, d.lastName].filter(Boolean).join(" ").trim();
        return d.EmployeeName || joined || u.EmployeeName || u.userName || "Unknown user";
    };

    downloadPdf = () => {
        const rows = this.getFilteredOrders();
        if (!rows.length) return;

        // Landscape: eight columns including an address and an item list do not
        // fit portrait A4 without wrapping into unreadable slivers.
        const doc = new jsPDF("l", "mm", "a4");

        // getWidth/getHeight over the bare .width/.height properties, which are
        // the pre-1.5 spelling; both exist on 1.5.3 but only the accessors are
        // guaranteed forward.
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.getWidth ? pageSize.getWidth() : pageSize.width;
        const pageHeight = pageSize.getHeight ? pageSize.getHeight() : pageSize.height;

        const MARGIN = 12;
        const usableWidth = pageWidth - MARGIN * 2;

        // Column widths are derived from the printable width rather than
        // hardcoded millimetres. Fixed widths that happen to over-sum push the
        // last column past the page edge, which is what made cell content spill
        // outside its borders.
        const weights = [4, 13, 15, 11, 20, 11, 18, 8]; // sums to 100
        const columnStyles = {};
        weights.forEach((weight, index) => {
            columnStyles[index] = { cellWidth: (usableWidth * weight) / 100 };
        });
        columnStyles[0].halign = "center";
        columnStyles[7].halign = "right";

        const statusLabel = this.state.activeStatus || "All statuses";
        const dateLabel = this.state.deliveryDate
            ? `Delivery date ${this.state.deliveryDate.toLocaleDateString()}`
            : "All delivery dates";
        const typeLabel = (
            ORDER_TYPE_OPTIONS.find((o) => o.value === this.state.orderType) || ORDER_TYPE_OPTIONS[0]
        ).label;

        doc.autoTable({
            // Space above the table is reserved by margin.top; the heading is
            // painted in didDrawPage so it repeats on every page.
            margin: { top: 30, left: MARGIN, right: MARGIN, bottom: 16 },
            theme: "grid",
            tableWidth: usableWidth,

            // Monochrome throughout: black text, grey hairline rules, and no
            // fill on any row. `false` disables the fill rather than painting
            // white over it, so the output stays a plain printable document.
            styles: {
                font: "helvetica",
                fontSize: 8.5,
                cellPadding: 2.2,
                overflow: "linebreak",
                valign: "top",
                textColor: 0,
                lineColor: 120,
                lineWidth: 0.1,
                fillColor: false,
            },
            headStyles: {
                fontStyle: "bold",
                textColor: 0,
                fillColor: false,
                lineColor: 120,
                lineWidth: 0.1,
            },
            bodyStyles: { fillColor: false },
            alternateRowStyles: { fillColor: false },
            columnStyles,

            head: [[
                "#", "Order No", "Customer", "Phone",
                "Delivery Address", "Delivery Date", "Items", "Amount (Rs.)",
            ]],
            body: rows.map((row, index) => [
                index + 1,
                pdfText(pick(row, FIELD_KEYS.orderNum)),
                pdfText(pick(row, FIELD_KEYS.customerName)),
                pdfText(pick(row, FIELD_KEYS.phone)),
                pdfText(pick(row, FIELD_KEYS.address)),
                pdfDate(pick(row, FIELD_KEYS.deliveryDate)),
                pdfText(formatItems(row, this.props.MenuItem)),
                pdfAmount(pick(row, FIELD_KEYS.amount)),
            ]),

            didDrawPage: () => {
                doc.setTextColor(0);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(13);
                doc.text("Order List", MARGIN, 14);

                doc.setFont("helvetica", "normal");
                doc.setFontSize(8.5);
                doc.text(`${statusLabel}  |  ${typeLabel}  |  ${dateLabel}  |  ${rows.length} order(s)`, MARGIN, 20);
                doc.text(
                    `Generated by ${this.generatedBy()} on ${new Date().toLocaleString()}`,
                    MARGIN,
                    25,
                );

                doc.setDrawColor(120);
                doc.setLineWidth(0.1);
                doc.line(MARGIN, 27, pageWidth - MARGIN, 27);
            },
        });

        // Footers are stamped after the fact: during didDrawPage the total page
        // count is not known yet, so "Page 1 of 1" would print on every page.
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i += 1) {
            doc.setPage(i);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(0);

            const label = `Page ${i} of ${pageCount}`;
            // Right-aligned by measurement rather than by the {align:'right'}
            // option, whose support varies across jsPDF 1.x builds.
            const labelWidth =
                (doc.getStringUnitWidth(label) * 8) / doc.internal.scaleFactor;
            doc.text(label, pageWidth - MARGIN - labelWidth, pageHeight - 8);
        }

        doc.save(`order-list-${dayKey(new Date())}.pdf`);
    };

    renderCard = (row, index) => {
        const { bg, color } = statusStyle(row.status);
        const items = formatItems(row, this.props.MenuItem);
        const time = pick(row, FIELD_KEYS.deliveryTime);

        return (
            <div className="ol-card" key={row.requestId || index}>
                <div className="ol-card-top">
                    <div className="ol-num">{show(pick(row, FIELD_KEYS.orderNum))}</div>
                    {row.status ? (
                        <span className="ol-badge" style={{ background: bg, color }}>
                            {row.status}
                        </span>
                    ) : null}
                </div>

                <div className="ol-row">
                    <i className="pi pi-user" />
                    <span>{show(pick(row, FIELD_KEYS.customerName))}</span>
                </div>

                <div className="ol-row">
                    <i className="pi pi-phone" />
                    <span>{show(pick(row, FIELD_KEYS.phone))}</span>
                </div>

                {/* One address only: the response carries resdeliveryaddress
                    and no separate customer address. */}
                <div className="ol-row">
                    <i className="pi pi-map-marker" />
                    <span>{show(pick(row, FIELD_KEYS.address))}</span>
                </div>

                <div className="ol-row">
                    <i className="pi pi-calendar" />
                    <span>
                        {fmtDate(pick(row, FIELD_KEYS.deliveryDate))}
                        {time ? ` · ${time}` : ""}
                    </span>
                </div>

                {items ? <div className="ol-items">{items}</div> : null}

                <div className="ol-card-bottom">
                    <div className="ol-price">{fmtMoney(pick(row, FIELD_KEYS.amount))}</div>
                    <div className="ol-actions">
                        <Button
                            icon="pi pi-eye"
                            label="View"
                            className="p-button-sm p-button-outlined"
                            onClick={() => this.setState({ viewOrder: row })}
                        />
                        <Button
                            icon="pi pi-pencil"
                            label="Edit"
                            className="p-button-sm"
                            onClick={() => this.goToEdit(row)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    renderViewDialog = () => {
        const row = this.state.viewOrder;
        if (!row) return null;

        const time = pick(row, FIELD_KEYS.deliveryTime);
        const items = getItems(row, this.props.MenuItem);

        const field = (label, value) => (
            <div>
                <div className="ol-dt">{label}</div>
                <div className="ol-dd">{value}</div>
            </div>
        );

        return (
            <Dialog
                header={`Order ${show(pick(row, FIELD_KEYS.orderNum))}`}
                visible
                // Percentage width with a breakpoint override: near full-bleed
                // on a phone, a centred panel on a laptop.
                style={{ width: "92vw", maxWidth: 640 }}
                breakpoints={{ "640px": "96vw" }}
                onHide={() => this.setState({ viewOrder: null })}
                dismissableMask
            >
                <div className="ol-dl">
                    {field("Order No", show(pick(row, FIELD_KEYS.orderNum)))}
                    {field("Status", show(row.status))}
                    {field("Customer Name", show(pick(row, FIELD_KEYS.customerName)))}
                    {field("Phone", show(pick(row, FIELD_KEYS.phone)))}
                    {field(
                        "Delivery Date",
                        `${fmtDate(pick(row, FIELD_KEYS.deliveryDate))}${time ? ` · ${time}` : ""}`,
                    )}
                    {field("Payment Status", show(pick(row, FIELD_KEYS.paymentStatus)))}
                </div>

                <div style={{ marginTop: 12 }}>
                    {field("Delivery Address", show(pick(row, FIELD_KEYS.address)))}
                </div>

                <div style={{ marginTop: 14 }}>
                    <div className="ol-dt">Menu Items</div>
                    {items.length ? (
                        <table className="ol-itable">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th className="ol-n">Qty</th>
                                    <th className="ol-n">Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it, i) => (
                                    <tr key={`${it.name}-${i}`}>
                                        <td>{it.name}</td>
                                        <td className="ol-n">{show(it.qty)}</td>
                                        <td className="ol-n">{fmtMoney(it.price)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="ol-dd">{DASH}</div>
                    )}
                </div>

                <div
                    style={{
                        marginTop: 16,
                        paddingTop: 12,
                        borderTop: "1px solid #ece3db",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        flexWrap: "wrap",
                    }}
                >
                    <div className="ol-price">{fmtMoney(pick(row, FIELD_KEYS.amount))}</div>
                    <Button
                        icon="pi pi-pencil"
                        label="Edit Order"
                        className="p-button-sm"
                        onClick={() => {
                            const target = row;
                            this.setState({ viewOrder: null }, () => this.goToEdit(target));
                        }}
                    />
                </div>
            </Dialog>
        );
    };

    render() {
        const tabs = this.getTabs();
        const filtered = this.getFilteredOrders();
        const visible = filtered.slice(0, this.state.visibleCount);

        const counts = {
            total: filtered.length,
            running: filtered.filter((o) => o.status === "Running").length,
            closed: filtered.filter((o) => o.status === "Closed").length,
            cancelled: filtered.filter((o) => o.status === "Cancelled").length,
        };

        return (
            <div className="ol-root">
                <style>{STYLES}</style>

                {this.props.loading && <div className="page-loader" />}

                <div className="ol-bar">
                    <h2 className="ol-title">Orders</h2>
                    <div className="ol-bar-actions">
                        <Button
                            icon="pi pi-file-pdf"
                            label="PDF"
                            className="p-button-sm p-button-outlined"
                            disabled={!filtered.length}
                            onClick={this.downloadPdf}
                        />
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-sm p-button-outlined"
                            onClick={this.handleRefresh}
                        />
                    </div>
                </div>

                <div className="ol-stats">
                    <div className="ol-stat">
                        <div className="ol-stat-label">Orders</div>
                        <div className="ol-stat-value">{counts.total}</div>
                    </div>
                    <div className="ol-stat">
                        <div className="ol-stat-label">Running</div>
                        <div className="ol-stat-value" style={{ color: "#ff5a1f" }}>{counts.running}</div>
                    </div>
                    <div className="ol-stat">
                        <div className="ol-stat-label">Closed</div>
                        <div className="ol-stat-value" style={{ color: "#1d6b39" }}>{counts.closed}</div>
                    </div>
                    <div className="ol-stat">
                        <div className="ol-stat-label">Cancelled</div>
                        <div className="ol-stat-value" style={{ color: "#a32020" }}>{counts.cancelled}</div>
                    </div>
                </div>

                {/* <div className="ol-tabs">
                    {tabs.map((tab) => (
                        <div
                            key={tab.value || "all"}
                            className={`ol-tab${this.state.activeStatus === tab.value ? " ol-tab-active" : ""}`}
                            onClick={() =>
                                this.setState({ activeStatus: tab.value, visibleCount: PAGE_SIZE })
                            }
                        >
                            {tab.label}
                        </div>
                    ))}
                </div> */}

                <div className="ol-search">
                    <span className="p-input-icon-left ol-search-field">
                        <i className="pi pi-search" />
                        <InputText
                            placeholder="Search order, customer, phone, item"
                            value={this.state.search}
                            onChange={(e) =>
                                this.setState({ search: e.target.value, visibleCount: PAGE_SIZE })
                            }
                        />
                    </span>
                    {this.state.search ? (
                        <Button
                            icon="pi pi-times"
                            className="p-button-sm p-button-outlined"
                            onClick={() => this.setState({ search: "", visibleCount: PAGE_SIZE })}
                        />
                    ) : null}
                </div>

                <div className="ol-filters">
                    <div className="ol-type">
                        <Dropdown
                            inputId="ol-order-type"
                            value={this.state.orderType}
                            options={ORDER_TYPE_OPTIONS}
                            onChange={(e) =>
                                this.setState({ orderType: e.value || "", visibleCount: PAGE_SIZE })
                            }
                            placeholder="Order type"
                        />
                    </div>

                    <div className="ol-date">
                        <Calendar
                            inputId="ol-delivery-date"
                            value={this.state.deliveryDate}
                            onChange={(e) =>
                                this.setState({
                                    deliveryDate: e.value || null,
                                    visibleCount: PAGE_SIZE,
                                })
                            }
                            dateFormat="dd/mm/yy"
                            placeholder="Delivery date"
                            showIcon
                            readOnlyInput
                        />
                    </div>

                    {this.state.deliveryDate ? (
                        <Button
                            label="All dates"
                            icon="pi pi-times"
                            className="p-button-sm p-button-outlined"
                            onClick={() =>
                                this.setState({ deliveryDate: null, visibleCount: PAGE_SIZE })
                            }
                        />
                    ) : (
                        <Button
                            label="Today"
                            icon="pi pi-calendar"
                            className="p-button-sm p-button-outlined"
                            onClick={() =>
                                this.setState({ deliveryDate: new Date(), visibleCount: PAGE_SIZE })
                            }
                        />
                    )}

                    {this.state.deliveryDate ? null : (
                        <div className="ol-filter-note">Showing orders for every delivery date.</div>
                    )}
                </div>

                {this.renderProblemNotice()}

                {visible.length ? (
                    <div className="ol-grid">{visible.map(this.renderCard)}</div>
                ) : (
                    // Names the date when one is filtering, and offers the way
                    // out. With the picker defaulting to today, an empty list
                    // otherwise reads as a broken page rather than a filter.
                    <div className="ol-empty">
                        <div>
                            No orders found
                            {this.state.deliveryDate
                                ? ` for ${this.state.deliveryDate.toLocaleDateString()}`
                                : ""}
                            .
                        </div>
                        {this.state.deliveryDate ? (
                            <Button
                                label="Show all dates"
                                className="p-button-sm p-button-text"
                                style={{ marginTop: 8 }}
                                onClick={() =>
                                    this.setState({ deliveryDate: null, visibleCount: PAGE_SIZE })
                                }
                            />
                        ) : null}
                    </div>
                )}

                {/* Load-more rather than a pager: it keeps scroll position on a
                    phone, where a pager's page numbers are an awkward tap. */}
                {filtered.length > visible.length ? (
                    <div className="ol-more">
                        <Button
                            label={`Load more (${filtered.length - visible.length} left)`}
                            className="p-button-sm p-button-text"
                            onClick={() =>
                                this.setState((s) => ({ visibleCount: s.visibleCount + PAGE_SIZE }))
                            }
                        />
                    </div>
                ) : null}

                {this.renderViewDialog()}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    success: state.master.success,
    error: state.master.error,
    loading: state.master.loading,
    orderList: state.master.requestlistgendata,
    userDetail: state.auth.userDetail,
    // Only for the PDF's "Generated by" line — userDetail carries the name on
    // most logins, userObject is the fallback for the ones where it doesn't.
    userObject: state.auth.userObject,
    // Menu-item id -> name lookup for the resmenuitemN columns.
    MenuItem: state.keyword.MenuItem !== undefined ? state.keyword.MenuItem : [],
});

const mapDispatchToProps = (dispatch) => ({
    getOrderList: (id, processId, companyId) =>
        dispatch(actions.getrequestListgen(id, processId, companyId)),
    getMasterKeywordList: (keyword) => dispatch(actions.getMasterKeywordList(keyword)),
    onAuth: (u, p, isSignup, Ar, mobile) => dispatch(actions.auth(u, p, isSignup, Ar, mobile)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderList);
